import {
  IonCard,
  IonCardContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonText,
  useIonViewDidEnter,
} from "@ionic/react";
import React, { useCallback, useContext, useState } from "react";
import { useParams } from "react-router";
import {
  getFieldsByGroup,
  WorkOrder,
  formatData,
  getAllFields,
  UdvData,
} from "../models/workorders/WorkOrder";
import { decodeParam } from "../util/ApiHelper";
import OnlineStatus from "../components/OnlineStatus";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import { reactPlugin } from "../util/AppInsights";
import ApiError from "../components/ApiError";
import { TranslationMessagesContext } from "../util/Translations";
import { useWorkOrderStore } from "../store/wo-order";
import { useShallow } from "zustand/react/shallow";
import useGetWorkOrder from "../hooks/useGetWorkOrder";
import useGetMetaData from "../hooks/work-order/useGetMetaData";
import useGetCustomValues from "../hooks/work-order/useGetCustomValues";
import useGetCustomFields from "../hooks/work-order/useGetCustomFields";

const WorkOrderDetails: React.FC = () => {
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const { workOrderId } = useParams<{ workOrderId: string }>();
  let displayWo = decodeParam(workOrderId);
  const EAMDefaultDate = "0001-01-01T00:00:00";
  const [errorData, setErrorData] = useState("");
  const [showLoading, setShowLoading] = useState(false);

  const [customValErrorState, setCustomValErrorState] = useState(false);
  const [customFieldsErrorState, setCustomFieldsErrorState] = useState(false);
  const { trigger } = useGetWorkOrder();
  const { getMetaData } = useGetMetaData();
  const { getCustomValues } = useGetCustomValues();
  const { getCustomFields } = useGetCustomFields();

  //zustand store

  const workOrder = useWorkOrderStore((state) =>
    state.getWorkOrderById(displayWo)
  );
  const addUpdateWorkOrder = useWorkOrderStore(
    (state) => state.addUpdateWorkOrder
  );

  const groups = useWorkOrderStore(
    useShallow((state) => state.getGroupsForWo(displayWo))
  );

  const metadata = useWorkOrderStore((state) =>
    state.getMetaDataForWo(displayWo)
  );

  const customValues = useWorkOrderStore(
    useShallow((state) => state.getUdvListForWo(displayWo))
  );
  const customFields = useWorkOrderStore((state) =>
    state.getCustomElementColumnDetailForWo(displayWo)
  );

  const woToSerial = workOrder?.WriteWoToSerialFlag === true ? "yes" : "no";

  const metadataQuery = useCallback(async () => {
    if (metadata) return; //we have metaData in cache

    const response = await getMetaData(displayWo);
    if (response.isError) {
      setErrorData(response.data?.toString());
    }
  }, [displayWo, getMetaData, metadata]);

  const workOrderQuery = useCallback(async () => {
    if (workOrder) return; //we have this work order in cache

    const response = await trigger(displayWo);
    if (response.isError) {
      setErrorData(response.data?.toString());
    } else {
      addUpdateWorkOrder(response.data);
    }
  }, [addUpdateWorkOrder, displayWo, trigger, workOrder]);

  const customValuesQuery = useCallback(async () => {
    if (customValues) return; //we already have customValues in the cache
    const response = await getCustomValues(displayWo);
    if (response.status !== 200) {
      setCustomValErrorState(true);
    } else if (response.data) {
      setCustomValErrorState(false);
    }
  }, [customValues, displayWo, getCustomValues]);

  const customFieldsQuery = useCallback(async () => {
    if (customFields) return; //we have customFields in the cache
    const response = await getCustomFields(displayWo);
    if (response.status !== 200) {
      setCustomFieldsErrorState(true);
    } else if (response.data) {
      setCustomFieldsErrorState(false);
    }
  }, [customFields, displayWo, getCustomFields]);

  const setUpData = useCallback(async () => {
    // Show loading symbol
    setShowLoading(true);
    await workOrderQuery();
    await metadataQuery();
    await customValuesQuery();
    await customFieldsQuery();
    setShowLoading(false);
  }, [customFieldsQuery, customValuesQuery, metadataQuery, workOrderQuery]);

  // useEffect(() => {
  //   setUpData();
  // }, [setUpData]);

  useIonViewDidEnter(() => {
    setUpData();
  }, [setUpData]);

  let customFieldsValues = [] as any[];

  if (customFields && Object.keys(customFields).length > 0) {
    customFieldsValues = Object.values(customFields);
  }

  return (
    <>
      <IonHeader>
        <OnlineStatus />
      </IonHeader>
      <ApiError errorData={errorData} />
      <IonLoading
        isOpen={showLoading}
        onDidDismiss={() => setShowLoading(false)}
        duration={5000}
      />
      <>
        {!!metadata &&
          !!workOrder &&
          groups.length > 0 &&
          groups.map((group) => {
            const fields = getFieldsByGroup(metadata, group.GroupName);
            return (
              fields.length > 0 && (
                <IonCard key={group.GroupName + String(group.GroupOrder)}>
                  <IonCardContent>
                    <IonList lines="full">
                      {fields.map((field) => {
                        //The PropertyName of the field is a key of the work order.
                        switch (field.TableName) {
                          case "eq_locations":
                            if (field.PropertyName === "Description") {
                              field.PropertyName = "EquipmentDescription";
                            }
                            break;
                          case "eq_serial":
                            if (field.PropertyName === "Description") {
                              field.PropertyName = "SerialDescription";
                            }
                            break;
                          default:
                            break;
                        }
                        const data =
                          workOrder[field.PropertyName as keyof WorkOrder];
                        return (
                          <IonItem key={field.IdText}>
                            <IonLabel>
                              <p>{field.TranslatedIdText}</p>
                              {field.NetType === "DateTime" &&
                              data === EAMDefaultDate ? (
                                <></>
                              ) : field.PropertyName ===
                                "WriteWoToSerialFlag" ? (
                                <h3>{woToSerial}</h3>
                              ) : (
                                <h3>{formatData(data, field)}</h3>
                              )}
                            </IonLabel>
                          </IonItem>
                        );
                      })}
                    </IonList>
                  </IonCardContent>
                </IonCard>
              )
            );
          })}
        {/* In the case where there are no groups, show all fields on one card. */}
        {!!metadata && !!workOrder && groups.length === 0 && (
          <IonCard>
            <IonCardContent>
              <IonList lines="full">
                {getAllFields(metadata).map((field) => {
                  const data = workOrder[field.PropertyName as keyof WorkOrder];
                  return (
                    <IonItem key={field.IdText}>
                      <IonLabel>
                        <p>{field.TranslatedIdText}</p>
                        {field.NetType === "DateTime" &&
                        data === EAMDefaultDate ? (
                          <></>
                        ) : field.PropertyName === "WriteWoToSerialFlag" ? (
                          <h3>{woToSerial}</h3>
                        ) : (
                          <h3>{formatData(data, field)}</h3>
                        )}
                      </IonLabel>
                    </IonItem>
                  );
                })}
              </IonList>
            </IonCardContent>
          </IonCard>
        )}
        {/* Card containing custom fields */}
        <IonCard
          hidden={
            (!customFieldsErrorState || !customValErrorState) &&
            (!customFieldsValues || customFieldsValues.length < 1)
          }
        >
          <IonCardContent>
            {customFieldsErrorState ? (
              <IonText
                color={"danger"}
                style={{ padding: "16px", display: "inline-block" }}
              >
                {translatedMessages["ErrCustomFields"]?.MessageText ||
                  "Error retrieving custom fields"}
              </IonText>
            ) : (
              <IonList>
                {customValErrorState && (
                  <IonItem lines="none" key={`custom-tab-error-item`}>
                    <IonText color={"danger"} key={"custom-tab-error-text"}>
                      {translatedMessages["ErrCustomValues"]?.MessageText ||
                        "Error fetching custom values"}
                    </IonText>
                  </IonItem>
                )}
                {customFieldsValues
                  .sort((a, b) => {
                    return (
                      customFieldsValues.indexOf(a) -
                      customFieldsValues.indexOf(b)
                    );
                  })
                  .map((customField) => {
                    let valueOfField = "" as string | number;
                    if (customValues && customValues.length > 0) {
                      let matchingValue = customValues.filter((val: any) => {
                        return val.UdId === customField.UdId;
                      });
                      if (matchingValue.length > 0) {
                        valueOfField =
                          matchingValue[0][
                            customField.PropertyName as keyof UdvData
                          ];
                      }
                    }
                    return (
                      <IonItem
                        lines="full"
                        key={`custom-field-${customField.IdText}`}
                      >
                        <IonLabel
                          key={`custom-field-${customField.IdText}-input-label`}
                        >
                          <p>{customField.TranslatedIdText}</p>
                          <h6>
                            {customField.PropertyName === "DateValue" &&
                            valueOfField !== ""
                              ? new Date(valueOfField).toLocaleDateString()
                              : String(valueOfField)}
                          </h6>
                        </IonLabel>
                      </IonItem>
                    );
                  })}
              </IonList>
            )}
          </IonCardContent>
        </IonCard>
      </>
    </>
  );
};

export default withAITracking(
  reactPlugin,
  WorkOrderDetails,
  "WorkOrderDetails"
);
